/**
 * MenuItemCard Component
 * 
 * Displays a menu item with customizable layout, styling, and interactions
 * 
 * PERFORMANCE FIX (December 2025):
 * - Variants are now passed as props instead of fetched per-item
 * - This eliminates N+1 queries (50 items = 50 queries → 1 batched query)
 * - See: COMPREHENSIVE_PERFORMANCE_AUDIT_2025.md - Part 6
 * 
 * ENHANCEMENT (January 2026):
 * - Added popup modal for full item details
 * - Shows full description without truncation
 * - Better variant selection UX with descriptions
 */

import React, { useState, memo } from 'react';
import { PlusCircle, CheckCircle, Plus, Minus, ShoppingCart } from 'lucide-react';
import { useMenuTheme } from '@/hooks/useMenuTheme';
import { useCart } from '@/contexts/CartContext';
import { useTranslation } from 'react-i18next';
import { MenuItemRatingDisplay } from '@/components/MenuItemRatingDisplay';
import { RatingSystem } from '@/components/RatingSystem';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MenuItem, MenuItemVariant } from '@/types/index';
import type { ViewContentParams } from '@/types/metaPixel';

interface MenuItemCardProps {
  item: MenuItem;
  /** Variants passed from parent (batched fetch) - eliminates N+1 queries */
  variants?: MenuItemVariant[];
  enableOrdering?: boolean;
  enableRatings?: boolean;
  onTrackViewContent?: (params: ViewContentParams) => void;
  className?: string;
}

export function MenuItemCard({
  item,
  variants = [],
  enableOrdering = true,
  enableRatings = false,
  onTrackViewContent,
  className = '',
}: MenuItemCardProps) {
  const { customization, branch, getCardClasses, getCardStyles, getHeadingClasses, getBodyClasses, getTextStyles, getImageAspectClass } = useMenuTheme();
  const { addItem, removeItem, isInCart, items: cartItems, updateQuantity } = useCart();
  const { t, i18n } = useTranslation();
  
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [useBasePrice, setUseBasePrice] = useState(true); // Default to base price
  const [isRatingDialogOpen, setIsRatingDialogOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [ratingKey, setRatingKey] = useState(0);
  
  const selectedVariant = selectedVariantId ? variants.find(v => v.id === selectedVariantId) : null;
  const isAdded = isInCart(item.id);
  const layout = customization.layout.type;
  const { menuCard } = customization.components;
  
  // Get cart button color from theme
  const cartButtonColor = customization.components.floatingCart?.buttonColor 
    || branch.accent_color 
    || branch.primary_color 
    || '#3b82f6';

  // Get cart item for quantity display
  const cartItem = cartItems.find(ci => ci.menuItem.id === item.id);
  const quantity = cartItem?.quantity || 0;

  // Get the current description - use variant description if selected variant has one, otherwise fallback to item description
  const currentDescription = selectedVariant?.description || item.description;

  // Get the current price - use selected variant price or base price
  const currentPrice = selectedVariant ? selectedVariant.price : item.base_price;

  // Helper to format price
  function formatPriceNoZeros(price: number): string {
    return price % 1 === 0
      ? price.toLocaleString('en-US', { maximumFractionDigits: 0 })
      : price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  const handleCardClick = () => {
    if (onTrackViewContent) {
      onTrackViewContent({
        content_name: item.name,
        content_ids: [item.id],
        content_type: 'product',
        value: item.base_price,
        currency: branch.currency || 'USD',
      });
    }
    // Open detail modal when card is clicked
    setIsDetailModalOpen(true);
  };

  const handleAddToCart = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    // If a variant is selected, use it; otherwise add without variant (base price)
    addItem(item, selectedVariant || undefined);
  };

  const handleRemoveFromCart = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    removeItem(item.id);
  };

  const handleQuantityChange = (newQuantity: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (newQuantity <= 0) {
      removeItem(item.id);
    } else {
      updateQuantity(item.id, newQuantity);
    }
  };

  const handleRateClick = () => {
    setIsRatingDialogOpen(true);
  };

  const handleRatingSubmitted = () => {
    setRatingKey(prev => prev + 1);
    setIsRatingDialogOpen(false);
  };

  // Layout-specific classes
  const getContainerClass = () => {
    const baseClasses = getCardClasses();
    
    switch (layout) {
      case 'grid':
        return `${baseClasses} flex flex-col group relative overflow-hidden !p-0`; // Standard grid
      case 'photo-grid':
        return `${baseClasses} flex flex-col group relative overflow-hidden !p-0 shadow-md`; // Photo focus with shadow
      case 'gallery':
        return `${baseClasses} flex flex-col group relative overflow-hidden !p-0`; // Wide gallery
      case 'card':
        return `${baseClasses} flex flex-col group relative overflow-hidden !p-0 shadow-lg`; // Elevated card
      case 'masonry':
        return `${baseClasses} flex flex-col group relative overflow-hidden !p-0`; // Variable height
      case 'compact':
        return `${baseClasses} flex flex-row items-center gap-3 p-2 group relative`; // Horizontal compact
      default: // list
        return `${baseClasses} flex items-center gap-4 mb-2 group relative`;
    }
  };

  const getImageClass = () => {
    switch (layout) {
      case 'grid':
        return 'w-full aspect-square object-cover rounded-t-lg'; // Square images
      case 'photo-grid':
        return 'w-full aspect-[3/4] object-cover rounded-t-lg'; // Tall portrait images
      case 'gallery':
        return 'w-full aspect-[16/9] object-cover rounded-t-lg'; // Wide landscape images
      case 'card':
        return 'w-full aspect-[4/3] object-cover rounded-t-lg'; // Standard card ratio
      case 'compact':
        return 'w-20 h-20 rounded-lg object-cover flex-shrink-0'; // Small square thumbnail
      case 'masonry':
        return 'w-full aspect-auto object-cover rounded-t-lg'; // Natural image height
      default: // list
        return 'w-28 h-28 sm:w-32 sm:h-32 rounded-lg object-cover flex-shrink-0';
    }
  };

  // Get aspect ratio string for CLS prevention
  const getImageAspectRatio = () => {
    switch (layout) {
      case 'grid':
        return '1/1'; // Square
      case 'photo-grid':
        return '3/4'; // Portrait
      case 'gallery':
        return '16/9'; // Landscape
      case 'card':
        return '4/3'; // Standard card
      case 'compact':
        return '1/1'; // Square thumbnail
      case 'masonry':
        return undefined; // Natural height
      default: // list
        return '1/1'; // Square
    }
  };

  const getContentClass = () => {
    const hasImage = !!item.image_url;
    
    switch (layout) {
      case 'grid':
      case 'photo-grid':
      case 'gallery':
        // Add top padding for items without images to make room for the button at top-right
        return hasImage ? 'p-2 flex-grow flex flex-col min-w-0' : 'p-3 pt-14 flex-grow flex flex-col min-w-0 items-center justify-center';
      case 'card':
        return hasImage ? 'p-3 flex flex-col flex-1' : 'p-3 pt-14 flex flex-col flex-1 justify-center items-center';
      case 'compact':
        return 'flex-1 min-w-0 flex flex-col justify-center'; // Vertical layout in horizontal card
      case 'masonry':
        return hasImage ? 'p-2 flex flex-col min-w-0' : 'p-3 pt-14 flex flex-col min-w-0 items-center justify-center';
      default: // list
        return hasImage ? 'flex-1 min-w-0 py-1' : 'flex-1 min-w-0 text-center py-1';
    }
  };

  const getTitleClass = () => {
    switch (layout) {
      case 'grid':
      case 'photo-grid':
      case 'gallery':
        return 'font-bold text-sm mb-1 text-gray-900';
      case 'card':
        return 'font-bold text-base mb-1 text-gray-900';
      case 'compact':
        return 'font-bold text-sm text-gray-900';
      case 'masonry':
      case 'list':
        return 'font-bold text-base text-gray-900';
      default:
        return 'font-bold text-base text-gray-900';
    }
  };

  const getButtonContainerClass = () => {
    switch (layout) {
      case 'grid':
      case 'photo-grid':
      case 'gallery':
      case 'card':
      case 'masonry':
        return 'absolute top-2 end-2 z-10';
      case 'compact':
      case 'list':
        return 'flex-shrink-0 flex flex-col items-center gap-1';
      default:
        return 'flex-shrink-0 flex flex-col items-center gap-1';
    }
  };

  const getButtonSize = () => {
    switch (layout) {
      case 'grid':
      case 'photo-grid':
      case 'gallery':
      case 'card':
      case 'masonry':
        return 'w-9 h-9 sm:w-10 sm:h-10';
      case 'compact':
      case 'list':
        return 'w-8 h-8 sm:w-9 sm:h-9';
      default:
        return 'w-8 h-8 sm:w-9 sm:h-9';
    }
  };

  return (
    <>
      <div className={`${getContainerClass()} ${className}`} style={getCardStyles()}>
        <div onClick={handleCardClick} className="cursor-pointer flex flex-col flex-1">
          {/* Image - with progressive loading, CDN optimization, and explicit dimensions to prevent CLS */}
          {customization.layout.showImages && item.image_url && (
            <OptimizedImage
              src={item.image_url}
              alt={item.name}
              className={getImageClass()}
              containerClassName={getImageClass()}
              placeholderColor="#f3f4f6"
              aspectRatio={getImageAspectRatio()}
              layout={layout === 'grid' ? 'grid' : layout === 'compact' ? 'thumbnail' : 'card'}
            />
          )}

          {/* Content */}
          <div className={getContentClass()}>
            <h3 className={getTitleClass()}>
              {item.name}
            </h3>

            {/* Description - shows variant-specific description if selected, otherwise main description */}
            {menuCard.showDescription && currentDescription && (
              <p className="text-xs text-gray-500 mb-2">
                {currentDescription}
              </p>
            )}

            {/* Variants */}
            {variants.length > 0 && (
              <div
                className="flex gap-1.5 mt-2 flex-wrap w-full"
                role="group"
                aria-label={t('menu_item.choose_size') || 'Choose a size'}
              >
                {variants.map(variant => (
                  <button
                    key={variant.id}
                    type="button"
                    className={`flex flex-row items-center justify-center gap-1 px-3 py-1.5 rounded-full border text-xs font-medium focus:outline-none transition-all duration-200 ${
                      selectedVariantId === variant.id 
                        ? 'text-white border-primary' 
                        : 'bg-white border-gray-300'
                    }`}
                    style={selectedVariantId === variant.id ? { backgroundColor: cartButtonColor } : { color: cartButtonColor }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVariantId(variant.id);
                    }}
                    aria-label={`Select ${variant.size_name} for ${item.name}`}
                    aria-pressed={selectedVariantId === variant.id}
                    tabIndex={0}
                  >
                    {variant.size_name && variant.size_name.trim().toLowerCase() !== 'regular' && (
                      <span className={`font-medium${selectedVariantId === variant.id ? ' text-white' : ''}`}>
                        {variant.size_name}
                      </span>
                    )}
                    <span className={`font-bold notranslate${selectedVariantId === variant.id ? ' text-white' : ''}`} lang="en">
                      {formatPriceNoZeros(variant.price)}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Price */}
            <p
              className="font-bold mt-auto text-base"
              style={{ color: customization.colors.primary }}
            >
              <span className="notranslate" lang="en">
                {variants.length > 0 && selectedVariant ? formatPriceNoZeros(selectedVariant.price) : formatPriceNoZeros(item.base_price)}
              </span>
              <span className="text-sm font-medium ml-1">{branch.currency}</span>
            </p>

            {/* Ratings */}
            {enableRatings && menuCard.showRatings && (
              <div className="mt-1 w-full min-w-0">
                <MenuItemRatingDisplay
                  key={ratingKey}
                  menuItemId={item.id}
                  compact={true}
                  showRateButton={true}
                  onRateClick={handleRateClick}
                />
              </div>
            )}
          </div>
        </div>

        {/* Add/Remove Button */}
        {enableOrdering && (
          <div className={getButtonContainerClass()}>
            {isAdded ? (
              <button
                onClick={handleRemoveFromCart}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all hover:scale-110 active:scale-95 p-1"
                style={{ '--tw-ring-color': customization.colors.primary } as React.CSSProperties}
                aria-label={t('cart.remove_item') || 'Remove from cart'}
                tabIndex={0}
              >
                <CheckCircle
                  className={`${getButtonSize()} text-white drop-shadow-md`}
                  style={{ backgroundColor: customization.colors.primary, borderRadius: '50%' }}
                />
              </button>
            ) : (
              <button
                onClick={handleAddToCart}
                className="focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-all hover:scale-110 active:scale-95 p-1"
                aria-label={t('cart.add_item') || 'Add to cart'}
                tabIndex={0}
              >
                <PlusCircle className={`${getButtonSize()} text-gray-700 hover:text-gray-900 drop-shadow-sm`} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Rating Dialog */}
      {enableRatings && (
        <Dialog open={isRatingDialogOpen} onOpenChange={setIsRatingDialogOpen}>
          <DialogContent className="max-w-md w-full mx-auto" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle>
                {i18n.language === 'ar' ? `تقييم ${item.name}` : `Rate ${item.name}`}
              </DialogTitle>
              <DialogDescription>
                {i18n.language === 'ar'
                  ? 'شارك تجربتك مع هذا العنصر لمساعدة العملاء الآخرين.'
                  : 'Share your experience with this item to help other customers.'
                }
              </DialogDescription>
            </DialogHeader>
            <RatingSystem
              menuItemId={item.id}
              branchId={branch.id}
              menuItemName={item.name}
              allowMultipleRatings={branch.allow_multiple_ratings ?? false}
              onRatingSubmitted={handleRatingSubmitted}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Item Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent 
          className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-0" 
          dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>{item.name}</DialogTitle>
            <DialogDescription>{item.description || 'Item details'}</DialogDescription>
          </DialogHeader>

          {/* Item Image */}
          {item.image_url && (
            <div className="relative w-full aspect-[4/3] bg-gray-100">
              <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-cover"
              />
              {!item.is_available && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {t('dashboard.menu.unavailable') || 'Unavailable'}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Title and Price */}
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-xl font-bold text-gray-900 flex-1">
                {item.name}
              </h2>
              <p 
                className="text-xl font-bold flex-shrink-0"
                style={{ color: customization.colors.primary }}
              >
                <span className="notranslate" lang="en">
                  {formatPriceNoZeros(currentPrice)}
                </span>
                <span className="text-sm font-medium ms-1">{branch.currency}</span>
              </p>
            </div>

            {/* Full Description */}
            {currentDescription && (
              <p className="text-gray-600 text-sm leading-relaxed">
                {currentDescription}
              </p>
            )}

            {/* Variants Selection - Show base price as "Regular" option plus variants */}
            {variants.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  {t('dashboard.menu_item.choose_size') || 'Choose size'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {/* Base price option (Regular/Default) */}
                  <button
                    type="button"
                    className={`flex flex-col items-center px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                      selectedVariantId === null 
                        ? 'text-white border-transparent shadow-md' 
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    style={selectedVariantId === null 
                      ? { backgroundColor: cartButtonColor } 
                      : { color: cartButtonColor }
                    }
                    onClick={() => setSelectedVariantId(null)}
                  >
                    <span className="font-semibold">{t('dashboard.menu_item.regular') || 'Regular'}</span>
                    <span className="text-xs opacity-80 notranslate" lang="en">
                      {formatPriceNoZeros(item.base_price)} {branch.currency}
                    </span>
                  </button>
                  
                  {/* Other variant options */}
                  {variants.map(variant => (
                    <button
                      key={variant.id}
                      type="button"
                      className={`flex flex-col items-center px-4 py-2 rounded-xl border-2 text-sm font-medium transition-all duration-200 ${
                        selectedVariantId === variant.id 
                          ? 'text-white border-transparent shadow-md' 
                          : 'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      style={selectedVariantId === variant.id 
                        ? { backgroundColor: cartButtonColor } 
                        : { color: cartButtonColor }
                      }
                      onClick={() => setSelectedVariantId(variant.id)}
                    >
                      <span className="font-semibold">{variant.size_name}</span>
                      <span className="text-xs opacity-80 notranslate" lang="en">
                        {formatPriceNoZeros(variant.price)} {branch.currency}
                      </span>
                    </button>
                  ))}
                </div>
                
                {/* Show variant-specific description if different from main */}
                {selectedVariant?.description && selectedVariant.description !== item.description && (
                  <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                    <span className="font-medium text-gray-700">
                      {selectedVariant.size_name}:
                    </span>{' '}
                    {selectedVariant.description}
                  </div>
                )}
              </div>
            )}

            {/* Ratings in modal */}
            {enableRatings && (
              <div className="pt-2 border-t">
                <MenuItemRatingDisplay
                  key={ratingKey}
                  menuItemId={item.id}
                  compact={false}
                  showRateButton={true}
                  onRateClick={() => {
                    setIsDetailModalOpen(false);
                    setTimeout(() => setIsRatingDialogOpen(true), 200);
                  }}
                />
              </div>
            )}

            {/* Add to Cart Section */}
            {enableOrdering && item.is_available !== false && (
              <div className="pt-4 border-t">
                {quantity > 0 ? (
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 rounded-full px-2 py-1"
                      style={{ backgroundColor: `${cartButtonColor}15` }}
                    >
                      <button
                        onClick={(e) => handleQuantityChange(quantity - 1, e)}
                        className="p-2 rounded-full hover:bg-white/50 transition-colors"
                        aria-label={t('cart.decrease_quantity') || 'Decrease quantity'}
                      >
                        <Minus className="h-5 w-5" style={{ color: cartButtonColor }} />
                      </button>
                      <span className="text-lg font-bold w-8 text-center" style={{ color: cartButtonColor }}>
                        {quantity}
                      </span>
                      <button
                        onClick={(e) => handleQuantityChange(quantity + 1, e)}
                        className="p-2 rounded-full hover:bg-white/50 transition-colors"
                        aria-label={t('cart.increase_quantity') || 'Increase quantity'}
                      >
                        <Plus className="h-5 w-5" style={{ color: cartButtonColor }} />
                      </button>
                    </div>
                    <Button
                      onClick={() => setIsDetailModalOpen(false)}
                      className="px-6"
                      style={{ backgroundColor: cartButtonColor }}
                    >
                      {t('common.done') || 'Done'}
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => handleAddToCart()}
                    className="w-full py-6 text-base font-semibold gap-2"
                    style={{ backgroundColor: cartButtonColor }}
                  >
                    <ShoppingCart className="h-5 w-5" />
                    {t('dashboard.menu_item.add_to_cart', 'Add to Cart')}
                  </Button>
                )}
              </div>
            )}

            {/* Unavailable message */}
            {item.is_available === false && (
              <div className="pt-4 border-t">
                <div className="bg-red-50 text-red-600 rounded-lg p-4 text-center text-sm font-medium">
                  {t('dashboard.menu.item_unavailable_message') || 'This item is currently unavailable'}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
